/**
 * Compare the loaded entry with the current HTML before mounting the app.
 * A new bundle triggers at most one reload per target bundle/session.
 * The check is bounded to 1.5s; offline, failed HTTP and blocked storage keep
 * the current app usable. An offline opening may still use a cached bundle.
 * Works with the existing network-first SW and no-cache HTML response.
 */

/** PURO/testável: extrai o caminho do bundle de entrada (`/assets/index-<hash>.js`) de um HTML. */
export function pickEntryBundle(html: string): string | null {
  const m = html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/);
  return m ? m[0] : null;
}

/** Bundle principal que esta aba realmente executa, sem query/hash. */
export function getLoadedEntryBundle(): string | null {
  const loadedEl = document.querySelector('script[type="module"][src*="/assets/index-"]');
  return (loadedEl?.getAttribute("src") || "").split(/[?#]/)[0] || null;
}

export async function checkForUpdate(): Promise<boolean> {
  try {
    // normaliza (tira ?query/#hash) — senão um src com ?v=N nunca igualaria o `latest` (que vem
    // limpo do pickEntryBundle) → loop eterno de reload.
    const loaded = getLoadedEntryBundle();
    if (!loaded) return false; // dev (entry = /src/main.tsx) ou sem script de entrada → no-op
    const res = await fetch("/index.html", { cache: "no-store", signal: AbortSignal.timeout(1500) });
    if (!res.ok) return false;
    const latest = pickEntryBundle(await res.text());
    if (!latest || latest === loaded) return false; // já no bundle mais novo
    const key = "ondiet_reloaded_for_" + latest;
    if (sessionStorage.getItem(key)) return false; // já recarregou por este bundle nesta sessão
    sessionStorage.setItem(key, "1");
    window.location.reload();
    return true;
  } catch {
    // offline / sem sessionStorage / fetch bloqueado — silencioso de propósito
    return false;
  }
}

/** Resolve a possible reload before showing the app. Failures preserve boot. */
export async function mountAfterUpdateCheck(mount: () => void, check = checkForUpdate): Promise<void> {
  let reloading = false;
  try { reloading = await check(); } catch { /* best effort */ }
  if (!reloading) mount();
}
